import { useCallback, useMemo } from "react";

import { useWorkspace } from "#app/lib/data/workspace-context";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { RECENT_ENTITY_LIMIT } from "#app/lib/hooks/use-recent-entity-ids";
import { RECENT_ITEM_MAX_AGE_MS } from "#app/lib/hooks/use-recent-items";
import type { Omittable } from "@salestrak/ui";

/**
 * A place the viewer went to on the map — the Map module menu's "Recent"
 * rows. A visit is an opened pin — one holding a single project is that
 * project, any other pin is its address — or a project picked from the map
 * search. Two kinds share one list because the map treats them as one thing,
 * a spot to return to, ordered by the visit, newest first, so the two
 * interleave.
 *
 * Entries are display SNAPSHOTS, the way `useRecentItems` keeps its projects:
 * an address is not an entity the client holds anywhere, and a project opened
 * on the map lives in no workspace-wide store, so the row renders from what
 * was known at the visit and navigates by coordinates and id. A rename lands
 * on the next visit.
 */
export type RecentMapPlace = Omittable<{
  kind: "address" | "issue";
  /** The project id, or the address id (falling back to the coordinates). */
  id: string;
  name: string;
  lat: number | null;
  lng: number | null;
  addressId?: string | null;
  /** Projects only. */
  workflowId?: string | null;
  /** The project TYPE's icon — a project's display identity. Projects only. */
  iconName?: string | null;
  iconColor?: string | null;
  /** Epoch ms of the visit — recency expiry keys off it. */
  visitedAt: number;
}>;

export type RecentMapPlaceVisit = Omit<RecentMapPlace, "visitedAt">;

/** The address entry's id when the visit carried no address id. */
export function mapPlaceCoordinateId(lat: number, lng: number): string {
  return `${lat},${lng}`;
}

function samePlace(a: RecentMapPlaceVisit, b: RecentMapPlaceVisit): boolean {
  return a.kind === b.kind && a.id === b.id;
}

/**
 * The viewer's recent map places for the active workspace, newest first,
 * capped at the module-menu recents rung (`RECENT_ENTITY_LIMIT`) and aged out
 * on the shared recents window. Every hook on the key shares one state
 * (`usePersistedState`), so the search bar recording a pick and the menu
 * reading the list see the same rows in the same render.
 */
export function useRecentMapPlaces(): {
  places: RecentMapPlace[];
  recordVisit: (place: RecentMapPlaceVisit) => void;
} {
  const { activeWorkspace } = useWorkspace();
  const { state, update } = usePersistedState(
    `map-recents:${activeWorkspace?.workspaceId ?? ""}`,
    { places: [] as RecentMapPlace[] },
  );

  // The window is re-applied on read: storage only prunes on write, and a
  // session can outlive the window.
  const places = useMemo(() => {
    const cutoff = Date.now() - RECENT_ITEM_MAX_AGE_MS;
    return state.places.filter((place) => place.visitedAt >= cutoff);
  }, [state.places]);

  const recordVisit = useCallback(
    (place: RecentMapPlaceVisit) => {
      if (!place.id || !place.name) return;
      const cutoff = Date.now() - RECENT_ITEM_MAX_AGE_MS;
      update((current) => ({
        places: [
          { ...place, visitedAt: Date.now() },
          ...current.places.filter(
            (existing) =>
              !samePlace(existing, place) && existing.visitedAt >= cutoff,
          ),
        ].slice(0, RECENT_ENTITY_LIMIT),
      }));
    },
    [update],
  );

  return { places, recordVisit };
}

/** Writer only — for the surfaces that record a visit and never list them. */
export function useRecordMapPlaceVisit(): (place: RecentMapPlaceVisit) => void {
  return useRecentMapPlaces().recordVisit;
}
